/**
 * ============================================================================
 * RevenueTrendChart - LINE CHART WITH PERIOD SELECTOR
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. RECEIVES DATA VIA PROPS
 *    - Parent provides daily, weekly, monthly arrays
 *    - Parent manages selectedPeriod state
 *    - This component just displays what it's given
 *
 * 2. USEMEMO FOR CHART DATA
 *    - Chart data selection based on selectedPeriod
 *    - Memoize to prevent recalculation on every render
 *    - Only recalculate when period changes
 *
 * 3. RECHARTS CONFIGURATION
 *    - Using monotone curves for smooth lines
 *    - 3 lines: Gross Revenue (blue), Commission (red), Net (green)
 *    - Responsive container for mobile
 *
 * 4. EXPORT FUNCTIONALITY
 *    - Export button calls parent's onExport callback
 *    - Parent generates CSV from current data
 *    - Don't duplicate export logic here
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { RevenueTrend, RevenuePeriod } from '../../types/revenue';

interface RevenueTrendChartProps {
  dailyData: RevenueTrend[];
  weeklyData: RevenueTrend[];
  monthlyData: RevenueTrend[];
  selectedPeriod: RevenuePeriod;
  onPeriodChange: (period: RevenuePeriod) => void;
  onExport: () => void;
  source?: 'all' | 'spotts' | 'manual'; // Dynamic chart based on source
}

/**
 * Revenue trend line chart with period selector
 * - For 'manual' source: Shows only Revenue line (vendor gets 100%)
 * - For 'spotts' or 'all' source: Shows Gross Revenue, Commission, Net Earnings
 */
export function RevenueTrendChart({
  dailyData,
  weeklyData,
  monthlyData,
  selectedPeriod,
  onPeriodChange,
  onExport,
  source = 'all',
}: RevenueTrendChartProps) {
  // Select chart data based on period
  const chartData = useMemo(() => {
    const data = selectedPeriod === 'daily' ? dailyData :
                 selectedPeriod === 'weekly' ? weeklyData :
                 monthlyData;

    // Ensure we have data, otherwise return empty array
    return data && data.length > 0 ? data : [];
  }, [selectedPeriod, dailyData, weeklyData, monthlyData]);

  // Determine X-axis key based on period
  const xAxisKey = selectedPeriod === 'weekly' ? 'week' :
                   selectedPeriod === 'monthly' ? 'month' :
                   'date';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Revenue Trend</h3>
          <p className="text-sm font-normal text-gray-600">Track your earnings over time</p>
        </div>

        {/* Period Selector */}
        <div className="flex items-center space-x-2">
          {(['daily', 'weekly', 'monthly'] as const).map((period) => (
            <button
              key={period}
              onClick={() => onPeriodChange(period)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold capitalize transition-all ${
                selectedPeriod === period
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Legend - Dynamic based on source */}
      <div className="flex items-center space-x-4 mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-blue-500"></div>
          <span className="text-sm text-gray-700 font-semibold">
            {source === 'manual' ? 'Revenue' : 'Gross Revenue'}
          </span>
        </div>
        {source !== 'manual' && (
          <>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-red-500"></div>
              <span className="text-sm text-gray-700 font-semibold">Commission</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-700 font-semibold">Net Earnings</span>
            </div>
          </>
        )}
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 600 }}
              tickLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 600 }}
              tickLine={{ stroke: '#e5e7eb' }}
              tickFormatter={(value) => `${CURRENCY_SYMBOL}${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '12px',
                fontWeight: 600,
              }}
              formatter={(value: any) => `${CURRENCY_SYMBOL}${Number(value).toLocaleString()}`}
            />
            <Line
              type="monotone"
              dataKey={(item: any) => item.revenue || item.gross_revenue || 0}
              stroke="#3b82f6"
              strokeWidth={3}
              dot={{ fill: '#3b82f6', r: 5, strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 8 }}
              name={source === 'manual' ? 'Revenue' : 'Gross Revenue'}
            />
            {source !== 'manual' && (
              <>
                <Line
                  type="monotone"
                  dataKey="commission"
                  stroke="#ef4444"
                  strokeWidth={3}
                  dot={{ fill: '#ef4444', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 8 }}
                  name="Commission"
                />
                <Line
                  type="monotone"
                  dataKey={(item: any) => item.net || item.net_earnings || 0}
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ fill: '#10b981', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 8 }}
                  name="Net Earnings"
                />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-80 flex items-center justify-center text-gray-500">
          <p>No data available for selected period</p>
        </div>
      )}
    </div>
  );
}
